import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { MapContainer, Marker, Popup, TileLayer, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import L, { LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Types
import { FleetSummary } from '../../../../types/Fleetsummary';
import { dashboardContent } from '../../../../constants/Constants';

interface GlobalFleetMapProps {
  facilities: FleetSummary[];
  loading?: boolean;
  selectedFacilityId?: string | null;
  panFacilityId?: string | null;
  onSelectFacility: (facility: FleetSummary) => void;
}

const MAP_HEIGHT = 520;

const InvalidateMapSize: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    requestAnimationFrame(() => map.invalidateSize());
  }, [map]);
  return null;
};

const FitBounds: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (!coords.length) return;
    const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 5 });
  }, [coords, map]);
  return null;
};

// Pans/zooms to the selected facility without resetting the view on every
const PanToSelected: React.FC<{ position: LatLng | null }> = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 6), { animate: true });
  }, [position, map]);
  return null;
};

const ResetView: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  const handleReset = () => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 5, animate: true });
    }
  };
  return (
    <div className='leaflet-top leaflet-left' style={{ marginTop: 80, pointerEvents: 'auto' }}>
      <div className='leaflet-control leaflet-bar'>
        <a
          href='#'
          title={dashboardContent.map.resetToFullView}
          role='button'
          aria-label={dashboardContent.map.resetToFullView}
          onClick={(e) => {
            e.preventDefault();
            handleReset();
          }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, color: '#444', textDecoration: 'none' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' />
            <path d='M3 3v5h5' />
          </svg>
        </a>
      </div>
    </div>
  );
};

const makeFacilityBubbleIcon = (deviceCount: number, isActive: boolean) =>
  L.divIcon({
    className: '',
    html: `<div style="
      min-width:60px;
      height:26px;
      padding:0;
      border-radius:999px;
      background:#ffffff;
      color:#111827;
      display:flex;
      align-items:center;
      justify-content:center;
      gap:6px;
      font-size:12px;
      font-weight:700;
      line-height:1;
      box-shadow:0 2px 6px rgba(15, 23, 42, 0.25);
      border:2px solid ${isActive ? '#7c3aed' : '#dc2626'};
      white-space:nowrap;
    "><span style="width:7px;height:7px;border-radius:50%;background:${isActive ? '#7c3aed' : '#dc2626'};display:inline-block;"></span> ${deviceCount}</div>`,
    iconSize: [60, 26],
    iconAnchor: [30, 13],
    popupAnchor: [0, -14],
  });

const hasValidLocation = (facility: FleetSummary): boolean =>
  facility.location != null && Number.isFinite(Number(facility.location.latitude)) && Number.isFinite(Number(facility.location.longitude));

// Facilities can share the exact same coordinates
const spreadCoLocated = (facilities: FleetSummary[]): [number, number][] => {
  const groups: Record<string, number[]> = {};
  facilities.forEach((f, index) => {
    const key = `${Number(f.location?.latitude).toFixed(6)},${Number(f.location?.longitude).toFixed(6)}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(index);
  });

  const positions: [number, number][] = new Array(facilities.length);
  Object.values(groups).forEach((indices) => {
    const count = indices.length;
    indices.forEach((facilityIndex, position) => {
      const latitude = Number(facilities[facilityIndex].location?.latitude);
      const longitude = Number(facilities[facilityIndex].location?.longitude);
      if (count === 1) {
        positions[facilityIndex] = [latitude, longitude];
        return;
      }
      const angle = (position / count) * 2 * Math.PI - Math.PI / 2;
      const radius = 0.05; // wider than StockFacilityMap's — fleet data spans the globe, not one airport
      positions[facilityIndex] = [latitude + radius * Math.cos(angle), longitude + (radius * Math.sin(angle)) / Math.cos((latitude * Math.PI) / 180)];
    });
  });

  return positions;
};

export default function GlobalFleetMap({ facilities, loading, selectedFacilityId, panFacilityId, onSelectFacility }: GlobalFleetMapProps) {
  // Markers, coords and panIndex must stay index-parallel, so they all derive from this
  // filtered list rather than from `facilities` directly.
  const mappableFacilities = useMemo(() => facilities.filter(hasValidLocation), [facilities]);
  const coords = useMemo<[number, number][]>(() => spreadCoLocated(mappableFacilities), [mappableFacilities]);

  // Highlighting (bubble color, table row) still keys off selectedFacilityId — unchanged.
  // Pan/zoom now keys off panFacilityId instead, so it only fires on explicit clicks.
  const panIndex = useMemo(
    () => (panFacilityId ? mappableFacilities.findIndex((f) => f.facilityId === panFacilityId) : -1),
    [mappableFacilities, panFacilityId],
  );
  const panPosition = panIndex >= 0 && coords[panIndex] ? new L.LatLng(coords[panIndex][0], coords[panIndex][1]) : null;

  const center = coords.length > 0 ? L.latLng(coords[0][0], coords[0][1]) : L.latLng(20, 0);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', height: MAP_HEIGHT }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography variant='subtitle1' fontWeight={700}>
          {dashboardContent.titles.globalFleetMap}
        </Typography>
      </Box>

      <Box sx={{ height: MAP_HEIGHT - 49, position: 'relative' }}>
        {loading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
          </Box>
        ) : !coords.length ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              {dashboardContent.messages.noFleetData}
            </Typography>
          </Box>
        ) : (
          <MapContainer center={center} zoom={2} scrollWheelZoom zoomControl attributionControl style={{ height: '100%', width: '100%' }}>
            <InvalidateMapSize />
            <TileLayer attribution={dashboardContent.map.openStreetMapAttribution} url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
            <FitBounds coords={coords} />
            <PanToSelected position={panPosition} />
            <ResetView coords={coords} />

            {mappableFacilities.map((facility, index) => (
              <Marker
                key={`${facility.facilityId}-${index}`}
                position={coords[index]}
                icon={makeFacilityBubbleIcon(facility.deviceCount, facility.facilityId === selectedFacilityId)}
                zIndexOffset={facility.facilityId === selectedFacilityId ? 1000 : 0}
                eventHandlers={{ click: () => onSelectFacility(facility) }}
              >
                <LeafletTooltip direction='top' offset={[0, -14]}>
                  <Typography variant='caption' fontWeight={700} display='block'>
                    {facility.facilityName}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    {facility.stationIataCode} · {facility.deviceCount} devices
                  </Typography>
                </LeafletTooltip>
                <Popup>
                  <Typography variant='caption' fontWeight={700} display='block'>
                    {facility.facilityName}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    {facility.stationIataCode} · {facility.deviceCount} devices
                  </Typography>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </Box>
    </Paper>
  );
}
